/**
 * <load-more-products> — appends the next page of a collection into the existing grid.
 *
 * WHY: collection pages here must stay at products_per_page:12. Products carry up to 2,016
 * variants (Size x Colour x Heel height), and rendering 24-36 of them exceeds Shopify's render
 * limit and returns HTTP 500. Load-more keeps every RENDER at 12 products while giving the
 * shopper one continuous list, which is also the pattern Baymard's list-view research favours
 * over both numbered pagination and infinite scroll.
 *
 * HOW: Shopify's Section Rendering API (`?section_id=`), the same mechanism assets/facets.js
 * already uses to re-render this grid on filter changes. No app, no extra library.
 *
 * SEO / no-JS: the real <nav> pagination stays in the DOM with working ?page= links and is only
 * hidden once this element upgrades. Crawlers and no-JS visitors keep the paginated series.
 */
class LoadMoreProducts extends HTMLElement {
  connectedCallback() {
    this.button = this.querySelector('[data-load-more-button]');
    this.status = this.querySelector('[data-load-more-status]');
    if (!this.button) return;

    this.grid = document.getElementById('product-grid');
    if (!this.grid) return;

    // Only take over once we know JS works — otherwise the real pagination stays authoritative.
    const pagination = document.querySelector('.pagination-wrapper');
    if (pagination) pagination.classList.add('load-more-active');
    this.hidden = false;

    this.button.addEventListener('click', () => this.loadNext());
  }

  async loadNext() {
    const nextUrl = this.dataset.nextUrl;
    if (!nextUrl || this.loading) return;

    this.loading = true;
    this.button.setAttribute('aria-busy', 'true');
    this.button.classList.add('loading');

    try {
      const sectionId = this.dataset.sectionId;
      const url = new URL(nextUrl, window.location.origin);
      url.searchParams.set('section_id', sectionId);

      const res = await fetch(url.toString());
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const doc = new DOMParser().parseFromString(await res.text(), 'text/html');

      const newItems = doc.querySelectorAll('#product-grid > li');
      if (!newItems.length) throw new Error('no items in response');

      const firstNew = newItems[0];
      newItems.forEach((li) => this.grid.appendChild(li));

      // Keep the address bar honest so refresh/share/back land where the shopper is.
      const shownUrl = new URL(nextUrl, window.location.origin);
      shownUrl.searchParams.delete('section_id');
      window.history.replaceState({}, '', shownUrl.toString());

      // Hand focus to the first newly added card: without this, keyboard and screen-reader
      // users are silently left at the button with no idea anything appeared.
      const focusTarget = firstNew.querySelector('a');
      if (focusTarget) {
        focusTarget.setAttribute('tabindex', '-1');
        focusTarget.focus({ preventScroll: true });
      }
      if (this.status) {
        this.status.textContent = this.dataset.loadedText || 'More products loaded';
      }

      // The fetched page tells us whether anything remains.
      const nextLink = doc.querySelector('load-more-products[data-next-url]');
      const followingUrl = nextLink && nextLink.dataset.nextUrl;
      if (followingUrl) {
        this.dataset.nextUrl = followingUrl;
      } else {
        this.remove();
      }
    } catch (e) {
      // Fail visibly rather than silently: restore the real pagination so the shopper can still
      // page through, and stop pretending load-more works.
      console.error('load-more failed:', e);
      const pagination = document.querySelector('.pagination-wrapper');
      if (pagination) pagination.classList.remove('load-more-active');
      this.remove();
    } finally {
      this.loading = false;
      if (this.button) {
        this.button.removeAttribute('aria-busy');
        this.button.classList.remove('loading');
      }
    }
  }
}

if (!customElements.get('load-more-products')) {
  customElements.define('load-more-products', LoadMoreProducts);
}
